import { CommonDeal } from 'components/common/types/Deal.types';
import { DirectSalesProductCategoryExclusivityType } from 'components/common/types/DirectSalesCampaign.types';
import { CodeName } from 'components/common/types/CodeName.types';
import { DirectSalesExclusionFlag, DirectSalesExclusions } from 'components/common/types/DirectSalesDeal.types';

export const getExclusionCodesKey = (items: CodeName[]): string =>
  items
    .map((item) => item.code)
    .sort()
    .join('\0');

export const areDirectSalesExclusionsEqual = (backup: DirectSalesExclusions, current: DirectSalesExclusions): boolean =>
  getExclusionCodesKey(backup.organisations) === getExclusionCodesKey(current.organisations) &&
  getExclusionCodesKey(backup.productCategories) === getExclusionCodesKey(current.productCategories);

export const isCodeNameFieldChanged = (backupCode: string | undefined, currentCode: string | undefined): boolean =>
  backupCode !== currentCode;

export type CampaignPlanLinkInvalidatingFields = {
  isNewDeal: boolean;
  backupAdvertiserCode: string | undefined;
  currentAdvertiserCode: string | undefined;
  backupBrandCode: string | undefined;
  currentBrandCode: string | undefined;
  backupAgencyCode: string | undefined;
  currentAgencyCode: string | undefined;
  backupSpecialistCode: string | undefined;
  currentSpecialistCode: string | undefined;
};

export const buildCampaignPlanLinkInvalidatingFields = (
  isNewDeal: boolean,
  backupCommonDeal: CommonDeal,
  commonDeal: CommonDeal,
): CampaignPlanLinkInvalidatingFields => ({
  isNewDeal,
  backupAdvertiserCode: backupCommonDeal.advertiser?.code,
  currentAdvertiserCode: commonDeal.advertiser?.code,
  backupBrandCode: backupCommonDeal.brand?.code,
  currentBrandCode: commonDeal.brand?.code,
  backupAgencyCode: backupCommonDeal.agency?.code,
  currentAgencyCode: commonDeal.agency?.code,
  backupSpecialistCode: backupCommonDeal.specialist?.code,
  currentSpecialistCode: commonDeal.specialist?.code,
});

export const hasDirectSalesCampaignPlanLinkInvalidatingChangeFromFields = (
  fields: CampaignPlanLinkInvalidatingFields,
): boolean => {
  if (fields.isNewDeal) return false;

  return (
    isCodeNameFieldChanged(fields.backupAdvertiserCode, fields.currentAdvertiserCode) ||
    isCodeNameFieldChanged(fields.backupBrandCode, fields.currentBrandCode) ||
    isCodeNameFieldChanged(fields.backupAgencyCode, fields.currentAgencyCode) ||
    isCodeNameFieldChanged(fields.backupSpecialistCode, fields.currentSpecialistCode)
  );
};

export type AllocationInvalidatingCampaignFields = CampaignPlanLinkInvalidatingFields & {
  backupProductCategoryCode: string | undefined;
  currentProductCategoryCode: string | undefined;
  backupProductCategoryExclusivity: DirectSalesProductCategoryExclusivityType;
  currentProductCategoryExclusivity: DirectSalesProductCategoryExclusivityType;
  backupDuplicationExclusion: DirectSalesExclusionFlag;
  currentDuplicationExclusion: DirectSalesExclusionFlag;
  backupJuxtapositionExclusion: DirectSalesExclusionFlag;
  currentJuxtapositionExclusion: DirectSalesExclusionFlag;
  backupExclusions: DirectSalesExclusions;
  currentExclusions: DirectSalesExclusions;
  backupAdvertiserBlockExclusion: DirectSalesExclusionFlag;
  currentAdvertiserBlockExclusion: DirectSalesExclusionFlag;
  backupAgencyBlockExclusion: DirectSalesExclusionFlag;
  currentAgencyBlockExclusion: DirectSalesExclusionFlag;
  backupSpecialistBlockExclusion: DirectSalesExclusionFlag;
  currentSpecialistBlockExclusion: DirectSalesExclusionFlag;
};

export const buildAllocationInvalidatingCampaignFields = (
  isNewDeal: boolean,
  backupCommonDeal: CommonDeal,
  commonDeal: CommonDeal,
): AllocationInvalidatingCampaignFields => ({
  ...buildCampaignPlanLinkInvalidatingFields(isNewDeal, backupCommonDeal, commonDeal),
  backupProductCategoryCode: backupCommonDeal.productCategory?.code,
  currentProductCategoryCode: commonDeal.productCategory?.code,
  backupProductCategoryExclusivity: backupCommonDeal.productCategoryExclusivity,
  currentProductCategoryExclusivity: commonDeal.productCategoryExclusivity,
  backupDuplicationExclusion: backupCommonDeal.duplicationExclusion,
  currentDuplicationExclusion: commonDeal.duplicationExclusion,
  backupJuxtapositionExclusion: backupCommonDeal.juxtapositionExclusion,
  currentJuxtapositionExclusion: commonDeal.juxtapositionExclusion,
  backupExclusions: backupCommonDeal.exclusions,
  currentExclusions: commonDeal.exclusions,
  backupAdvertiserBlockExclusion: backupCommonDeal.advertiserBlockExclusion,
  currentAdvertiserBlockExclusion: commonDeal.advertiserBlockExclusion,
  backupAgencyBlockExclusion: backupCommonDeal.agencyBlockExclusion,
  currentAgencyBlockExclusion: commonDeal.agencyBlockExclusion,
  backupSpecialistBlockExclusion: backupCommonDeal.specialistBlockExclusion,
  currentSpecialistBlockExclusion: commonDeal.specialistBlockExclusion,
});

export const hasDirectSalesAllocationInvalidatingCampaignChangeFromFields = (
  fields: AllocationInvalidatingCampaignFields,
): boolean => {
  if (fields.isNewDeal) return false;

  return (
    hasDirectSalesCampaignPlanLinkInvalidatingChangeFromFields(fields) ||
    isCodeNameFieldChanged(fields.backupProductCategoryCode, fields.currentProductCategoryCode) ||
    fields.backupProductCategoryExclusivity !== fields.currentProductCategoryExclusivity ||
    fields.backupDuplicationExclusion !== fields.currentDuplicationExclusion ||
    fields.backupJuxtapositionExclusion !== fields.currentJuxtapositionExclusion ||
    !areDirectSalesExclusionsEqual(fields.backupExclusions, fields.currentExclusions) ||
    fields.backupAdvertiserBlockExclusion !== fields.currentAdvertiserBlockExclusion ||
    fields.backupAgencyBlockExclusion !== fields.currentAgencyBlockExclusion ||
    fields.backupSpecialistBlockExclusion !== fields.currentSpecialistBlockExclusion
  );
};
